import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {Attachment, BodyAndAttachments, Mail, MailFolderEnum, WebmailApi} from "components/src/api/portal/generated";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {Download} from "components/src/common/Download";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {ReloadButton} from "components/src/common/ReloadButton";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {DateUtils} from "components/src/utils/DateUtils";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from 'dompurify';
import {useEffect, useState} from "react";
import {Paperclip as AttachmentIcon, Reply as ReplyIcon, Trash as TrashIcon} from 'react-bootstrap-icons';
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ReplyPopup} from "./ReplyPopup";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());

export const Messages = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The selected folder
     */
    const [folder, setFolder] = useState<MailFolderEnum>(MailFolderEnum.Inbox);
    /**
     * The number of available messages
     */
    const [messageCount, setMessageCount] = useState<number>();
    /**
     * The loaded messages
     */
    const [messages, setMessages] = useState<Mail[]>([]);
    /**
     * The messages for which the checkbox is selected
     */
    const [selectedMessages, setSelectedMessages] = useState<Array<Mail>>([]);
    /**
     * The currently selected messages
     */
    const [currentMessage, setCurrentMessage] = useState<Mail | undefined>(undefined);
    /**
     * The HTML bodies of the selected message
     */
    const [bodyAndAttachments, setBodyAndAttachments] = useState<BodyAndAttachments | undefined>(undefined);
    /**
     * If true, the "confirm delete selected" modal dialog will be shown
     */
    const [showDeleteConfirmSelected, setShowDeleteConfirmSelected] = useState<boolean>(false);
    /**
     * If true, the "confirm delete current" modal dialog will be shown
     */
    const [showDeleteConfirmCurrent, setShowDeleteConfirmCurrent] = useState<boolean>(false);
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The number of messages to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    /**
     * If true, the licenses will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The attachment to download
     */
    const [attachment, setAttachment] = useState<Attachment>();
    /**
     * If true, the reply popup will be shown
     */
    const [showReplyPopup, setShowReplyPopup] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        webmailApi.webmailPortalControllerGetMessageCount(folder)
            .then(response => {
                setMessageCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting message count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t,
        folder]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        webmailApi.webmailPortalControllerGetMessages(folder, (currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setMessages(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting messages", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t,
        folder,
        currentPage,
        rowsPerPage]);

    useEffect(() =>
    {
        if (currentMessage === undefined) {
            return
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetBodyAndAttachments(folder, currentMessage.uid)
            .then((response) =>
            {
                setBodyAndAttachments(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting body and attachments", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [folder,
        navigate,
        currentMessage,
        setSpinnerStateCounter,
        t]);

    const deleteMessages = (messages : Array<Mail>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerDeleteMessages(folder, messages.map(message => message.uid))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting messages", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirmSelected(false);
                setShowDeleteConfirmCurrent(false);
                setSelectedMessages([]);
            })
    }

    /**
     * Called when the folder is changed
     */
    const onFolderChanged = (folder : MailFolderEnum) =>
    {
        setFolder(folder)
        // reset state
        setCurrentPage(1);
        setSelectedMessages([])
        setCurrentMessage(undefined);
        setBodyAndAttachments(undefined);
        setForceReload(true);
    }

    const onChangeSelect = (checked : boolean, selectedMessage : Mail) =>
    {
        if (checked) {
            setSelectedMessages([...selectedMessages, selectedMessage]);
        }
        else {
            setSelectedMessages(selectedMessages.filter(message => message.uid !== selectedMessage.uid));
        }
    }

    const invertSelection = () => {
        setSelectedMessages([...messages].filter(message => !selectedMessages.includes(message)));
    }

    const setReadStatus = (read : boolean) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerSetReadFlag(folder, read, selectedMessages.map(message => message.uid))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting read status", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setSelectedMessages([]);
            })
    }

    const seenCssClass = (message : Mail) =>
    {
        if (!message.seen) {
            return "tr-font-weight-bold";
        }

        return null;
    }

    const clickedCssClass = (message : Mail) =>
    {
        if (message.uid === currentMessage?.uid) {
            return "tr-secondary";
        }

        return null;
    }

    const onMessageClicked = (message : Mail) => {
        setCurrentMessage(message);
    }

    const downloadAttachment = (attachment : Attachment) =>
    {
        if (currentMessage === undefined) {
            // should not happen
            return;
        }

        setAttachment(attachment);

        webmailApi.webmailPortalControllerGetAttachment(folder, currentMessage.uid, attachment.index,
            {responseType : 'blob'}).then((response) =>
        {
            setBlob(new Blob([response.data], {type: "application/octet-stream"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error downloading attachment", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () =>
    {
        // disable the Download component
        setDownload(false);
    }

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html)};
    }

    const reply = () => {
        setShowReplyPopup(true);
    }

    /**
     * Is called when the reply popup is closed
     */
    const onReplyPopupClose = () => {
        setShowReplyPopup(false);
    }

    return (
        <>
            <Row>
                <Col>
                    <Navbar className="bg-body-tertiary">
                        <Pager itemCount={messageCount ?? 0}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />

                        <Nav className="ms-auto">
                            <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                         setCurrentPage={setCurrentPage}/>
                            <ReloadButton onClickReload={() => setForceReload(true)}/>
                        </Nav>
                    </Navbar>
                </Col>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Navbar className="bg-body-tertiary">
                        <ConfirmDialog title={t("common.confirm")}
                                       bodyText={t("messages.confirm-delete-selected-messages")}
                                       confirmButtonTitle={t("common.delete")}
                                       onClickConfirmEvent={() => deleteMessages(selectedMessages)}
                                       showConfirm={showDeleteConfirmSelected}
                                       setShowConfirm={setShowDeleteConfirmSelected}
                        />

                        <ConfirmDialog title={t("common.confirm")}
                                       bodyText={t("messages.confirm-delete-message")}
                                       confirmButtonTitle={t("common.delete")}
                                       onClickConfirmEvent={() => deleteMessages([currentMessage!])}
                                       showConfirm={showDeleteConfirmCurrent}
                                       setShowConfirm={setShowDeleteConfirmCurrent}
                        />

                        {showReplyPopup && currentMessage != undefined &&
                            <ReplyPopup sourceMail={currentMessage} sourceBody={bodyAndAttachments?.text} onClose={onReplyPopupClose}/>}

                        <Form>
                            <Row>
                                <Col xs="auto">
                                    <NavDropdown className="z-index-popover d-inline-flex align-middle" title={t("common.actions")}>
                                        <NavDropdown.Item disabled={selectedMessages.length === 0}
                                                          onClick={() => setShowDeleteConfirmSelected(true)}>
                                            {t("messages.delete-selected")}
                                        </NavDropdown.Item>
                                        <NavDropdown.Item disabled={selectedMessages.length === 0}
                                                          onClick={() => setReadStatus(true)}>
                                            {t("messages.mark-as-read")}
                                        </NavDropdown.Item>
                                        <NavDropdown.Item disabled={selectedMessages.length === 0}
                                                          onClick={() => setReadStatus(false)}>
                                            {t("messages.mark-as-unread")}
                                        </NavDropdown.Item>
                                    </NavDropdown>
                                </Col>
                                <Col xs="auto">
                                    <Form.Select size="sm"
                                                 aria-label={t("messages.folder")}
                                                 value={folder}
                                                 onChange={(e) => onFolderChanged(Utils.stringToEnum(MailFolderEnum, e.target.value as MailFolderEnum))}>
                                        <option value="INBOX">{t("messages.folder-inbox")}</option>
                                        <option value="SENT">{t("messages.folder-sent")}</option>
                                    </Form.Select>
                                </Col>
                                <Col xs="auto">
                                    <Button size="sm"
                                            variant="primary"
                                            onClick={() => reply()}
                                            disabled={currentMessage === undefined || folder != MailFolderEnum.Inbox || StringUtils.isBlank(currentMessage.from)}>
                                        <ReplyIcon size={"1em"}/> {t("messages.reply")}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button size="sm"
                                            variant="danger"
                                            onClick={() => setShowDeleteConfirmCurrent(true)}
                                            disabled={currentMessage === undefined}>
                                        <TrashIcon size={"1em"}/> {t("messages.delete")}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Navbar>
                </Col>
            </Row>
            <Row>
                <Table className="table-sm" bordered hover responsive>
                    <thead className="sticky-top">
                    <tr className="text-nowrap">
                        <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                        <th>{t("messages.subject")} </th>
                        <th>{t("messages.from")} </th>
                        <th>{t("messages.date")} </th>
                    </tr>
                    </thead>

                    <tbody>
                        {messages.map((message: Mail) => (
                            // make the tr focusable and execute on enter keypress
                            <tr key={message.uid}
                                    className={`text-nowrap ${seenCssClass(message)} ${clickedCssClass(message)}`}
                                    onClick={() => onMessageClicked(message)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => Utils.clickOnEnterKeyPressed(e)}>
                                <td className="td-w-min">
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedMessages.includes(message)}
                                        onChange={(e) => onChangeSelect(e.target.checked, message)}
                                        inline
                                    />
                                </td>
                                <td><Truncate value={message.subject} maxLength={120}/></td>
                                <td><Truncate value={message.from} maxLength={60}/></td>
                                <td>{DateUtils.timestampToDateStringShort(message.date)}</td>
                            </tr>
                        ))}
                        </tbody>
                </Table>
            </Row>

            <Row>
                {bodyAndAttachments?.attachments.map((attachment) => (
                    // make the div focusable and execute on enter keypress
                    <div key={attachment.index}
                         title={t("messages.download-attachment")}
                         onClick={() => downloadAttachment(attachment)}
                         role="button"
                         tabIndex={0}
                         onKeyDown={(e) => Utils.clickOnEnterKeyPressed(e)}>
                        <AttachmentIcon/> {attachment.filename}
                    </div>
                ))}
            </Row>

            <Row className="content-area overflow-scroll">
                {bodyAndAttachments?.html.map((htmlBody, index) => (
                    <div key={index} dangerouslySetInnerHTML={sanitizeHTML(htmlBody)} />
                ))}
            </Row>

            {download && blob !== undefined && (
                <Download blob={blob}
                          filename={attachment!.filename}
                          onDownload={onDownload} />
            )}
        </>
    );
}